import { MapPinIcon } from '@heroicons/react/20/solid';
import { useFormContext, useWatch } from 'react-hook-form';
import useSettings from '@/hooks/useSettings';
import { useCepSearch } from '@/hooks/useCepSearch';
import TextField from '@/components/common/TextField';
import Select from '@/components/common/Select';
import Spinner from '@/components/common/Spinner';
import { useEffect, useState } from 'react';
import { CheckIcon, ExclamationTriangleIcon } from '@heroicons/react/20/solid';
import { isNommi } from '@/utils/brand';

export type AddressFormValues = {
  zipCode: string;
  street: string;
  number: string;
  complement?: string;
  neighborhood: string;
  city: string;
  state: string;
};

const brazilianStates = [
  { value: 'AC', label: 'Acre' },
  { value: 'AL', label: 'Alagoas' },
  { value: 'AP', label: 'Amapá' },
  { value: 'AM', label: 'Amazonas' },
  { value: 'BA', label: 'Bahia' },
  { value: 'CE', label: 'Ceará' },
  { value: 'DF', label: 'Distrito Federal' },
  { value: 'ES', label: 'Espírito Santo' },
  { value: 'GO', label: 'Goiás' },
  { value: 'MA', label: 'Maranhão' },
  { value: 'MT', label: 'Mato Grosso' },
  { value: 'MS', label: 'Mato Grosso do Sul' },
  { value: 'MG', label: 'Minas Gerais' },
  { value: 'PA', label: 'Pará' },
  { value: 'PB', label: 'Paraíba' },
  { value: 'PR', label: 'Paraná' },
  { value: 'PE', label: 'Pernambuco' },
  { value: 'PI', label: 'Piauí' },
  { value: 'RJ', label: 'Rio de Janeiro' },
  { value: 'RN', label: 'Rio Grande do Norte' },
  { value: 'RS', label: 'Rio Grande do Sul' },
  { value: 'RO', label: 'Rondônia' },
  { value: 'RR', label: 'Roraima' },
  { value: 'SC', label: 'Santa Catarina' },
  { value: 'SP', label: 'São Paulo' },
  { value: 'SE', label: 'Sergipe' },
  { value: 'TO', label: 'Tocantins' }
];

interface AddressFormProps {
  className?: string;
}

const AddressForm: React.FC<AddressFormProps> = ({ className = '' }) => {
  const settings = useSettings();
  const { searchCep, isLoadingCep, handleManualChange, resetManualChanges } = useCepSearch();
  const { setValue } = useFormContext();
  const zipCode = useWatch({ name: 'zipCode' });
  const city = useWatch({ name: 'city' });
  const state = useWatch({ name: 'state' });
  const [cepFound, setCepFound] = useState(false);
  const [cepError, setCepError] = useState(false);
  const [lastSearchedCep, setLastSearchedCep] = useState('');

  // Effect to handle CEP search
  useEffect(() => {
    if (zipCode && zipCode.replace(/\D/g, '').length === 8) {
      const normalizedCep = zipCode.replace(/\D/g, '');
      if (normalizedCep !== lastSearchedCep) {
        setLastSearchedCep(normalizedCep);
        setCepError(false);
        setCepFound(false);
        resetManualChanges();
        searchCep(zipCode);
      }
    } else if (zipCode && zipCode.replace(/\D/g, '').length < 8) {
      setCepFound(false);
      setCepError(false);
      setLastSearchedCep('');
    }
  }, [zipCode, searchCep, resetManualChanges]);

  // Effect to detect when CEP was found or failed
  useEffect(() => {
    if (!isLoadingCep && lastSearchedCep && zipCode?.replace(/\D/g, '') === lastSearchedCep) {
      if (city && state) {
        setCepFound(true);
        setCepError(false);
      } else if (zipCode?.replace(/\D/g, '').length === 8) {
        setCepError(true);
        setCepFound(false);
      }
    }
  }, [isLoadingCep, city, state, lastSearchedCep, zipCode]);

  const hasValidCep = zipCode && zipCode.replace(/\D/g, '').length === 8;
  const showAddressFields = hasValidCep || zipCode?.length > 0;

  return (
    <div className={`w-full ${className}`}>
      <div className="flex items-center mb-4 font-bold">
          {
            !isNommi && (
              <MapPinIcon
                style={{ color: settings.text?.color.primary }}
                width={24}
                height={24}
                className="mr-2"
              />
            )
          }

        <span style={{ color: settings.text?.color.primary }} className="text-lg">
          {
            isNommi ? "Endereço de entrega" : "Endereço"
          }
        </span>
      </div>

      <div className="flex flex-col gap-4">
        <div className="relative">
          <TextField
            name="zipCode"
            size="sm"
            label="CEP"
            placeholder="00000-000"
            mask="99999-999"
            inputMode="numeric"
          />
          <div className="absolute right-3 top-8 flex items-center">
            {isLoadingCep && <Spinner className="w-4 h-4" />}
            {cepFound && !isLoadingCep && <CheckIcon className="w-4 h-4 text-green-500" />}
            {cepError && !isLoadingCep && hasValidCep && (
              <ExclamationTriangleIcon className="w-4 h-4 text-yellow-500" />
            )}
          </div>
        </div>

        {showAddressFields && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="sm:col-span-2">
                <TextField
                  name="street"
                  size="sm"
                  label="Logradouro"
                  placeholder="Rua, Avenida, etc."
                  normalize
                  onChange={() => handleManualChange('street')}
                />
              </div>
              <div>
                <TextField name="number" size="sm" label="Número" placeholder="123" normalize />
              </div>
            </div>
            <TextField
              name="complement"
              size="sm"
              label="Complemento (opcional)"
              placeholder="Apartamento, bloco, etc."
              normalize
            />
            <TextField
              name="neighborhood"
              size="sm"
              label="Bairro"
              placeholder="Nome do bairro"
              normalize
              onChange={() => handleManualChange('neighborhood')}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <TextField
                name="city"
                size="sm"
                label="Cidade"
                placeholder="Nome da cidade"
                normalize
                onChange={() => handleManualChange('city')}
              />
              <Select
                name="state"
                size="sm"
                label="Estado"
                placeholder="Selecione o estado"
                options={brazilianStates}
                onChange={(e) => {
                  setValue('state', e.target.value);
                  handleManualChange('state');
                }}
              />
            </div>

            {cepError && hasValidCep && (
              <div className="flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <ExclamationTriangleIcon className="w-4 h-4 text-yellow-600" />
                <span className="text-sm text-yellow-800">
                  CEP não encontrado. Preencha os campos manualmente.
                </span>
              </div>
            )}
          </>
        )}
      </div>
      {

        isNommi && (
          <>
            <div className="flex items-center font-bold mt-6">
              <span style={{ color: settings.text?.color.primary }} className="text-lg">
                Método de Envio
              </span>
            </div>
            <div className="flex flex-col gap-4">
              <Select
                name="freight"
                size="sm"
                label="Opções de envio"
                placeholder="Selecione uma opção"
                options={[{ value: "15", label: "Frete disponível: SEDEX – R$ 15,00 – entrega em até 7 dias úteis"}, { value: "10", label: "Frete disponível: Melhor Envio – R$ 10,00 – entrega em até 5 dias úteis"}]}
                onChange={(e) => {
                  setValue('freight', e.target.value);
                }}
              />
            </div>
          </>
        )
      }

    </div>
  );
};

export default AddressForm;
